import { createSlice } from '@reduxjs/toolkit'
import {
  filterBySummary,
  filterByUserType,
  usersPagination,
  retrieveUsers,
  retrieveCompanies,
  retrieveUserByID,
  updateSelectedUserStore,
  updateTenantID,
  usersSortBy,
  searchUsers,
  createUsers,
  editUser,
  filterByCompanyID,
  userChangeStatus,
  sysAdminUsersResetAlertHandler,
} from './sysAdminUsersThunk'

const defaultPageSize = 9
const typesList = [
  {
    name: 'Application Users',
    value: 'APPLICATION_USER',
  },
  {
    name: 'Employee Users',
    value: 'EMPLOYEE_USER',
  },
  {
    name: 'Customer Admin Users',
    value: 'CUSTOMER_ADMIN_USER',
  },
  {
    name: 'System Admin Users',
    value: 'SYS_ADMIN_USER',
  },
]
const initialState = {
  loading: false,
  pageNumber: 0,
  pageSize: defaultPageSize,
  finalPage: false,
  summaries: {},
  activeSummary: '',
  sortBy: [],
  userTypes: typesList,
  activeUserTypes: [],
  companyIDs: [],
  activeSort: {},
  activeSearch: '',
  value: [],
  companiesValue: [],
  updatedUser: {},
  tenant_id: null,
  alert: {},
}

const usersSlice = createSlice({
  name: 'users',
  initialState,

  extraReducers: {
    [sysAdminUsersResetAlertHandler.fulfilled]: (state) => {
      state.alert = {}
    },

    [retrieveUsers.pending]: (state) => {
      state.loading = true
      state.finalPage = true
    },
    [retrieveUsers.fulfilled]: (state, { payload }) => {
      state.summaries = payload.metadata
      state.value = payload.users
      state.updatedUser = {}
      state.activeUserTypes = []
      state.companyIDs = []
      state.sortBy = payload.sort
      state.activeSort = payload.sort[0] || {}
      state.activeSummary = Object.keys(payload.metadata)[0].toUpperCase()
      state.finalPage = payload.users.length < state.pageSize
      state.loading = false
    },
    [retrieveCompanies.pending]: (state) => {
      state.loading = true
      state.finalPage = true
    },
    [retrieveCompanies.fulfilled]: (state, { payload }) => {
      state.loading = false
      state.companiesValue = payload
    },
    [retrieveUserByID.pending]: (state) => {
      state.loading = true
      state.finalPage = true
    },
    [retrieveUserByID.fulfilled]: (state, { payload }) => {
      state.loading = false
      state.updatedUser = payload
      state.updatedUser.departments = payload.employee?.departments
    },
    [updateSelectedUserStore.fulfilled]: (state, { payload }) => {
      state.loading = false
      state.updatedUser = payload
    },
    [updateTenantID.pending]: (state) => {
      state.loading = false
    },
    [updateTenantID.fulfilled]: (state, { payload }) => {
      state.loading = false
      state.tenant_id = payload
    },
    [searchUsers.pending]: (state) => {
      state.loading = true
      state.finalPage = true
    },
    [searchUsers.fulfilled]: (state, { payload }) => {
      state.activeSearch = payload.search
      state.finalPage = payload.data.users.length < state.pageSize
      state.pageNumber = 0
      state.summaries = payload.data.metadata
      state.sortBy = payload.data.sort
      state.value = payload.data.users
      state.loading = false
    },
    [filterBySummary.pending]: (state) => {
      state.loading = true
      state.finalPage = true
    },
    [filterBySummary.fulfilled]: (state, { payload }) => {
      state.pageNumber = 0
      state.summaries = payload.data.metadata
      state.activeSummary = payload.summary
      state.sortBy = payload.data.sort
      state.value = payload.data.users
      state.finalPage = payload.data.users.length < state.pageSize
      state.loading = false
    },
    [filterByUserType.pending]: (state) => {
      state.loading = true
      state.finalPage = true
    },
    [filterByUserType.fulfilled]: (state, { payload }) => {
      state.pageNumber = 0
      state.activeUserTypes = payload.userTypes
      state.summaries = payload.data.metadata
      state.activeSummary = Object.keys(payload.data.metadata)[0].toUpperCase()
      state.sortBy = payload.data.sort
      state.activeSort = payload.data.sort[0] || {}
      state.value = payload.data.users
      state.finalPage = payload.data.users.length < state.pageSize
      state.loading = false
    },
    [filterByCompanyID.pending]: (state) => {
      state.loading = true
      state.finalPage = true
    },
    [filterByCompanyID.fulfilled]: (state, { payload }) => {
      state.pageNumber = 0
      state.value = payload.data.users
      state.summaries = payload.data.metadata
      state.activeSummary = Object.keys(payload.data.metadata)[0].toUpperCase()
      state.sortBy = payload.data.sort
      state.activeSort = payload.data.sort[0] || {}
      state.finalPage = payload.data.users.length < state.pageSize
      state.companyIDs = payload.companyIDs
      state.loading = false
    },
    [usersPagination.pending]: (state) => {
      state.loading = true
      state.finalPage = true
    },
    [usersPagination.fulfilled]: (state, { payload }) => {
      state.finalPage = payload.data.users.length < state.pageSize
      state.sortBy = payload.data.sort
      state.pageNumber = payload.pageNumber
      state.value.push(...payload.data.users)
      state.loading = false
    },
    [usersSortBy.pending]: (state) => {
      state.loading = true
      state.finalPage = true
    },
    [usersSortBy.fulfilled]: (state, { payload }) => {
      state.value = payload.data.users
      state.finalPage = payload.data.users.length < state.pageSize
      state.pageNumber = 0
      state.sortBy = payload.data.sort
      state.activeSort = payload.sort
      state.loading = false
    },
    [createUsers.pending]: (state) => {
      state.loading = true
      state.alert = { }
    },
    [createUsers.rejected]: (state, { payload }) => {
      state.loading = false
      state.alert = payload
    },
    [createUsers.fulfilled]: (state, { payload }) => {
      state.loading = false
      state.updatedUser = {}
      state.alert = payload
    },
    [editUser.pending]: (state) => {
      state.loading = true
      state.alert = { }
    },
    [editUser.fulfilled]: (state, { payload }) => {
      state.loading = false
      state.updatedUser = {}
      state.alert = payload
    },
    [editUser.rejected]: (state, { payload }) => {
      state.loading = false
      state.alert = payload
    },
    [userChangeStatus.pending]: (state) => {
      state.loading = true
      state.finalPage = true
    },
    [userChangeStatus.fulfilled]: (state) => {
      state.pageNumber = 0
      state.loading = false
    },
    [userChangeStatus.rejected]: (state) => {
      state.loading = false
    },
  },
})

export const sysAdminSelectUsers = ({ sysAdminUsers }) => sysAdminUsers

export default usersSlice.reducer
